/**
 * Age du catalogue et etats « catalogue perime » (design, section 3).
 *
 * L'age se mesure depuis la derniere confirmation par le serveur
 * (catalog_meta.confirmed_at : 200 verifie ou 304), avec l'horloge du poste :
 * la PR 1.3 ne mesure pas l'ecart avec l'horloge du serveur (question ouverte).
 *
 *   - moins de 24 h : a jour ;
 *   - de 24 h a 72 h : pastille ambre « Prix du <date> » ;
 *   - 72 h et plus : pastille rouge « Prix de plus de 3 jours : un responsable
 *     doit confirmer », et la vente est bloquee jusqu'au NIP d'un responsable ;
 *   - une confirmation dans le futur de plus de 5 minutes (horloge du poste
 *     reculee) compte comme 72 h et plus : on ne contourne pas le blocage en
 *     reculant l'horloge.
 *
 * Confirmation d'un responsable : valable pour CET instantane du catalogue et
 * 24 heures (question ouverte : duree a valider par Mathieu). Une nouvelle
 * confirmation par le serveur la rend inutile.
 */

export const HOUR_MS = 60 * 60_000;
export const WARNING_AFTER_MS = 24 * HOUR_MS;
export const BLOCK_AFTER_MS = 72 * HOUR_MS;
export const CLOCK_TOLERANCE_MS = 5 * 60_000;
export const MANAGER_CONFIRMATION_TTL_MS = 24 * HOUR_MS;

export const FRESHNESS = Object.freeze({
    MISSING: 'missing',
    FRESH: 'fresh',
    WARNING: 'warning',
    EXPIRED: 'expired',
});

const dayFormat = new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'long', timeZone: 'America/Toronto' });

/** « 2 novembre », jour de la confirmation, a l'heure de Toronto. */
export function formatPriceDate(iso) {
    return dayFormat.format(new Date(iso));
}

/** La confirmation d'un responsable couvre-t-elle ce catalogue a `nowMs` ? */
export function isManagerConfirmationValid(confirmation, catalogMeta, nowMs) {
    if (!confirmation || !catalogMeta) return false;
    if (confirmation.snapshot_id !== catalogMeta.snapshot_id) return false;
    const at = Date.parse(confirmation.confirmed_at);
    if (!Number.isFinite(at)) return false;
    if (at - nowMs > CLOCK_TOLERANCE_MS) return false;
    return nowMs - at < MANAGER_CONFIRMATION_TTL_MS;
}

/**
 * @param {object|null} catalogMeta enregistrement de catalog_meta
 * @param {number} nowMs
 * @param {object|null} [managerConfirmation] meta.catalog_manager_confirmation
 * @returns {{ state: string, ageMs: number|null, confirmedAt: string|null,
 *   managerConfirmed: boolean, label: string|null }}
 */
export function catalogFreshness(catalogMeta, nowMs, managerConfirmation = null) {
    const confirmedAt = catalogMeta && catalogMeta.confirmed_at;
    const at = confirmedAt ? Date.parse(confirmedAt) : NaN;
    if (!Number.isFinite(at)) {
        return { state: FRESHNESS.MISSING, ageMs: null, confirmedAt: null, managerConfirmed: false, label: null };
    }
    const ageMs = nowMs - at;
    let state;
    if (ageMs < -CLOCK_TOLERANCE_MS || ageMs >= BLOCK_AFTER_MS) state = FRESHNESS.EXPIRED;
    else if (ageMs >= WARNING_AFTER_MS) state = FRESHNESS.WARNING;
    else state = FRESHNESS.FRESH;

    const managerConfirmed = state === FRESHNESS.EXPIRED && isManagerConfirmationValid(managerConfirmation, catalogMeta, nowMs);
    let label = null;
    if (state === FRESHNESS.WARNING) label = `Prix du ${formatPriceDate(confirmedAt)}`;
    if (state === FRESHNESS.EXPIRED) {
        label = managerConfirmed
            ? 'Prix de plus de 3 jours : confirmés par un responsable'
            : 'Prix de plus de 3 jours : un responsable doit confirmer';
    }
    return { state, ageMs, confirmedAt, managerConfirmed, label };
}
